import type {
  OrderCartV2Line,
  OrderCartV2Output,
} from "../../services/redux/types/OrderCartV2Types";

/**
 * Whether `result` is the quote for the cart as it is now: it came from the
 * latest calculate dispatched, and it priced exactly these lines at these
 * quantities.
 *
 * The reducer drops a response whose version is no longer the latest, so while
 * a newer calculate is in flight the stored result is the previous one and the
 * lines already hold what the guest just added. Placing the order from that
 * pair sends the new lines with the old total -- an order priced 40 was
 * created as free because the quote still described the reward-only cart.
 *
 * The version alone is not enough: a mutation that does not reach calculate
 * (no token) or a reducer that edits lines after the response (an INVALID
 * offer's lines are removed) leaves the version matching lines the quote never
 * priced. So the lines are compared too, both ways.
 */
export function isLoyaltyQuoteCurrent(
  result: OrderCartV2Output | undefined,
  latestCartVersion: number,
  lines: Pick<OrderCartV2Line, "lineId" | "quantity">[]
): boolean {
  if (!result?.pricing || result.cartVersion !== latestCartVersion) {
    return false;
  }

  const quoted = new Map(
    (result.lines ?? []).map((line) => [line.lineId, line.quantity])
  );

  return (
    quoted.size === lines.length &&
    lines.every(
      (line) => !!line.lineId && quoted.get(line.lineId) === line.quantity
    )
  );
}
